import BaseController from "@base/baseController.js";
import TagManager from "./tagManager.js";
import api from "@api/api.js";

/**
 * Tag module controller — the single door other modules use to reach the tag
 * vocabulary (the gallery grid for display names, the gallery editor for its
 * lookup field, the Cockpit for editing them).
 *
 * There is no page of its own: tags are shown by the gallery views and managed
 * from Cockpit → Tag Manager.
 */
class TagController extends BaseController {
  constructor() {
    super({ manager: new TagManager() });
    this._loaded = false;
  }

  async ensureLoaded() {
    if (!this._loaded) {
      await this.manager.load();
      this._loaded = true;
    }
  }

  /** Force a reload on next access (after an edit, or a language change). */
  invalidate() {
    this._loaded = false;
  }

  /** `{ key: label }` for the given language, loading the vocabulary first. */
  async labels(lang) {
    await this.ensureLoaded();
    return this.manager.labels(lang);
  }

  async vocabulary(lang) {
    await this.ensureLoaded();
    return this.manager.vocabulary(lang);
  }

  // --- admin operations (all admin-gated server-side) ---

  /** Create a tag before anything uses it; the backend refuses a duplicate key. */
  create({ key, nameEn, nameDe }) {
    return api.post("/api/tags", { key, nameEn, nameDe });
  }

  updateNames(key, { nameEn, nameDe }) {
    return api.put(`/api/tags/${encodeURIComponent(key)}`, { nameEn, nameDe });
  }

  /** Rename when `to` is free, merge when it already exists — one call either way. */
  retag(key, to) {
    return api.post(`/api/tags/${encodeURIComponent(key)}/retag`, { to });
  }

  remove(key) {
    return api.del(`/api/tags/${encodeURIComponent(key)}`);
  }
}

export default new TagController();
